import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { History, RefreshCw, Filter, X, Ship, User, ChevronDown, Search, CalendarDays } from "lucide-react";

const API = window.location.origin;
const PAGE = 50;

const ACTION_META = {
  cell_edit:    { label: "Hücre Düzenleme", color: "#2563EB", icon: "fa-pen" },
  boat_assign:  { label: "Tekne Atama",     color: "#0891B2", icon: "fa-ship" },
  auto_assign:  { label: "Otomatik Dağıt",  color: "#059669", icon: "fa-wand-magic-sparkles" },
  checkin:      { label: "Check-in",        color: "#16A34A", icon: "fa-user-check" },
  cancel:       { label: "İptal",           color: "#DC2626", icon: "fa-ban" },
  reschedule:   { label: "Tarih Değişimi",  color: "#7C3AED", icon: "fa-calendar-day" },
  tags:         { label: "Etiket",          color: "#DB2777", icon: "fa-tag" },
  delete:       { label: "Silme",           color: "#B91C1C", icon: "fa-trash" },
  lock_date:    { label: "Tarih Kilitleme", color: "#B45309", icon: "fa-lock" },
  unlock_date:  { label: "Kilit Açma",      color: "#0D9488", icon: "fa-lock-open" },
  hotel_override:{ label: "Otel Tipi",      color: "#4F46E5", icon: "fa-hotel" },
  manual_add:   { label: "Yeni Rezervasyon", color: "#0EA5E9", icon: "fa-plus" },
  connection_lost: { label: "Bağlantı Kaybı", color: "#EA580C", icon: "fa-wifi" },
  save_failed:  { label: "Kaydedilemedi",  color: "#DC2626", icon: "fa-triangle-exclamation" },
  review_add:   { label: "Yorum Eklendi",  color: "#D97706", icon: "fa-star" },
  review_import:{ label: "Yorum Aktarımı", color: "#D97706", icon: "fa-file-import" },
  review_match: { label: "Yorum Eşleşti",  color: "#0D9488", icon: "fa-link" },
  review_unmatch:{ label: "Yorum Eşleşme Kaldırıldı", color: "#B45309", icon: "fa-link-slash" },
  review_delete:{ label: "Yorum Silindi",  color: "#B91C1C", icon: "fa-trash" },
  review_source_connect:{ label: "Yorum Kaynağı Bağlandı", color: "#0D9488", icon: "fa-plug" },
  review_sync:  { label: "Yorum Çekimi",   color: "#D97706", icon: "fa-rotate" },
};

const ROLE_LABEL = { admin: "Super Admin", super_admin: "Super Admin", manager: "Manager", staff: "Garson", garson: "Garson" };

// Ham DB kolon adlarını insan diline çevirir (Detay sütununda "ne değişti" netliği)
const FIELD_LABELS = {
  masa_no: "Salon / Masa No", masa_no_2: "Masa No (2)", alt_no: "Alt Masa No",
  garson: "Garson", katilim: "Katılım (kişi)", notes: "Açıklama",
  tutar: "Kalan Ödeme", odeme: "Ödenen Tutar", tahsilat_sekli: "Ödeme Şekli",
  pp_fiyat: "P.P Fiyat", comp: "Comp", yildiz: "Yıldız", kalkis: "Kalkış",
  cari: "Cari", fin_m1: "M-1 (₺)", fin_m2: "M-2 (₺)", toplam: "Toplam",
  rez_alan: "Rezervasyonu Alan", status: "Durum",
  yemeksiz: "Yemeksiz", m1: "M-1", m2: "M-2", limit: "Limit", vip: "VIP",
  vip_alkol: "VIP Alkol", yarim: "Çocuk", inf: "İnfant", snack: "Snack", yemekli: "Yemekli",
  // Booking/kimlik sütun anahtarları (satır-içi manuel düzenleme)
  otel_adi: "Otel", otel: "Otel", hotel_name: "Otel",
  acenta: "Acenta", agency: "Acenta",
  isim: "İsim (müşteri)", customer_name: "İsim (müşteri)",
  telefon: "Telefon", customer_phone: "Telefon",
  kod: "Onay Kodu", confirmation_code: "Onay Kodu",
  opsiyon: "Opsiyon", option_text: "Opsiyon", option_name: "Opsiyon",
  saat: "Saat", booking_time: "Saat",
  pax: "Pax (kişi)", total_pax: "Pax (kişi)",
};
const fieldLabel = (f) => FIELD_LABELS[f] || f || "-";

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const emptyV = (v) => (v === null || v === undefined || v === "" ? "(boş)" : String(v));

const MENU_LABELS = { yemeksiz: "Yemeksiz", m1: "M-1", m2: "M-2", limit: "Limit", vip: "VIP", vip_alkol: "VIP Alkol", yarim: "Çocuk", inf: "İnfant", snack: "Snack", yemekli: "Yemekli" };

// Yeni rezervasyon: tüm girilen alanları çip listesi olarak göster
function ManualAddDetails({ d }) {
  if (!d) return "Yeni manuel rezervasyon eklendi";
  const chips = [];
  const add = (label, val) => {
    if (val === null || val === undefined || val === "" || (Array.isArray(val) && !val.length)) return;
    chips.push({ label, val });
  };
  add("İsim", d.isim);
  add("Kod", d.kod);
  add("Telefon", d.telefon);
  add("E-posta", d.email);
  add("Pax", d.pax);
  add("Yetişkin", d.yetiskin);
  add("Çocuk", d.cocuk);
  add("Bebek", d.bebek);
  add("Opsiyon", d.opsiyon);
  add("Otel", d.otel);
  add("Saat", d.saat);
  add("Acenta", d.acenta);
  add("Salon", d.salon);
  add("Masa No", d.masa_no);
  add("Garson", d.garson);
  add("Katılım", d.katilim);
  add("Açıklama", d.aciklama);
  const menuStr = d.menu && Object.keys(d.menu).length
    ? Object.entries(d.menu).map(([k, v]) => `${MENU_LABELS[k] || k}: ${v}`).join(", ") : null;
  add("Menü", menuStr);
  const exStr = Array.isArray(d.eklentiler) && d.eklentiler.length
    ? d.eklentiler.map(e => `${e.name}${e.qty ? " ×" + e.qty : ""}`).join(", ") : null;
  add("Eklentiler", exStr);
  if (!chips.length) return "Yeni manuel rezervasyon eklendi";
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((c, i) => (
        <span key={i} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-slate-700/60 border border-slate-600/50">
          <span className="text-slate-400">{c.label}:</span>
          <span className="text-sky-300 font-medium">{String(c.val)}</span>
        </span>
      ))}
    </div>
  );
}

// Detay: "ne, neden nereye" — insan diliyle
function describe(log) {
  const a = log.action;
  if (a === "cell_edit") {
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="text-slate-400">{fieldLabel(log.field)}:</span>
        <span className="px-1.5 py-0.5 rounded bg-slate-700/70 text-slate-300 line-through decoration-slate-500">{emptyV(log.old_value)}</span>
        <i className="fa-solid fa-arrow-right-long text-[10px] text-slate-500" />
        <span className="px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 font-medium">{emptyV(log.new_value)}</span>
      </span>
    );
  }
  if (a === "boat_assign") return `${log.count ?? ""} rezervasyon taşındı`;
  if (a === "auto_assign") return `${log.count ?? 0} rezervasyon${log.redistribute ? " yeniden" : ""} dağıtıldı${log.all_future ? " (tüm ileri tarihler)" : ""}`;
  if (a === "checkin") return log.checked_in ? "Geldi olarak işaretlendi" : "Check-in kaldırıldı";
  if (a === "cancel") return log.cancelled ? `İptal edildi${log.reason ? " — " + log.reason : ""}` : "İptal geri alındı";
  if (a === "reschedule") return `Tarih: ${emptyV(log.from)} → ${emptyV(log.to)}`;
  if (a === "tags") return `Etiketler: ${Array.isArray(log.tags) && log.tags.length ? log.tags.join(", ") : "(temizlendi)"}`;
  if (a === "delete") return "Rezervasyon silindi";
  if (a === "lock_date") return "Tarih kilitlendi";
  if (a === "unlock_date") return "Tarih kilidi açıldı";
  if (a === "hotel_override") return `Otel tipi: "${emptyV(log.value)}"`;
  if (a === "review_add") return `Yorum #${log.review_id ?? ""} eklendi (${log.source || ""}${log.booking_ref ? " · " + log.booking_ref : ""})`;
  if (a === "review_import") return `${log.inserted ?? 0} yeni yorum aktarıldı, ${log.matched ?? 0} eşleşti (${log.source || ""}${log.file ? " · " + log.file : ""})`;
  if (a === "review_match") return `Yorum #${log.review_id ?? ""} → ${log.confirmation_code || "#" + (log.booking_id ?? "")}`;
  if (a === "review_unmatch") return `Yorum #${log.review_id ?? ""} eşleşmesi kaldırıldı`;
  if (a === "review_delete") return `Yorum #${log.review_id ?? ""} silindi`;
  if (a === "review_source_connect") return `${(log.source || "").toUpperCase()} bağlandı (${log.account || ""})`;
  if (a === "review_sync") return `${(log.source || "").toUpperCase()} ${log.mode === "full" ? "tüm geçmiş" : "yeni yorumlar"} çekimi başlatıldı`;
  if (a === "manual_add") {
    if (log.details) return <ManualAddDetails d={log.details} />;
    return log.field ? `Yeni rezervasyon eklendi (${fieldLabel(log.field)}: ${emptyV(log.new_value)})` : "Yeni manuel rezervasyon eklendi";
  }
  if (a === "connection_lost") {
    const sec = log.duration_ms ? Math.round(log.duration_ms / 1000) : null;
    return sec != null ? `Bağlantı ${sec} sn koptu, yeniden bağlandı` : "Bağlantı koptu, yeniden bağlandı";
  }
  if (a === "save_failed") {
    const what = log.field ? fieldLabel(log.field) : "";
    const reason = log.reason === "network" || log.reason === "manual_create_network" ? "ağ hatası"
      : log.queued ? "bağlantı yok (kuyruğa alındı)"
      : log.reason === "manual_create" ? "yeni kayıt eklenemedi" : (log.detail || "kaydedilemedi");
    return `KAYDEDİLEMEDİ${what ? " — " + what : ""}${log.value != null && log.value !== "" ? ` = "${log.value}"` : ""} (${reason})`;
  }
  return "";
}

// Müşteri / Rezervasyon hücresi
function CustomerCell({ log }) {
  const hasCustomer = log.customer_name || log.confirmation_code;
  if (!hasCustomer) return <span className="text-slate-600 text-xs">—</span>;
  return (
    <div className="min-w-0">
      <div className="text-slate-200 truncate">{log.customer_name || "(isimsiz)"}</div>
      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        {log.confirmation_code && <span className="font-mono">{log.confirmation_code}</span>}
        {(log.total_pax !== null && log.total_pax !== undefined) && (
          <span className="inline-flex items-center gap-0.5"><i className="fa-solid fa-user text-[9px]" /> {log.total_pax}</span>
        )}
      </div>
    </div>
  );
}

export default function AuditLog() {
  const [tab, setTab] = useState("user"); // 'user' | 'cancel'
  const [logs, setLogs] = useState([]);
  const [cancels, setCancels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(null);
  const [reliab, setReliab] = useState(null);
  const [fUser, setFUser] = useState("");
  const [fAction, setFAction] = useState("");
  const [fDate, setFDate] = useState("");
  const [search, setSearch] = useState("");
  const [staff, setStaff] = useState([]);

  const token = localStorage.getItem("auth_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Kullanıcı dropdown listesi
  useEffect(() => {
    axios.get(`${API}/api/staff`, { headers })
      .then(r => setStaff(r.data?.staff || []))
      .catch(() => setStaff([]));
  }, []); // eslint-disable-line

  const fetchCancels = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fDate) params.date = fDate;
      const res = await axios.get(`${API}/api/cancel-history`, { headers, params });
      setCancels(res.data?.events || []);
    } catch { setCancels([]); }
    setLoading(false);
  }, [fDate]); // eslint-disable-line

  const fetchLogs = useCallback(async (offset = 0) => {
    if (offset === 0) setLoading(true); else setLoadingMore(true);
    try {
      const params = { offset, limit: PAGE };
      if (fUser) params.user_email = fUser;
      if (fAction) params.action = fAction;
      if (fDate) params.date = fDate;
      const res = await axios.get(`${API}/api/audit-logs`, { headers, params });
      const items = res.data?.logs || [];
      setHasMore(!!res.data?.has_more);
      setTotal(res.data?.total ?? null);
      setLogs(prev => offset === 0 ? items : [...prev, ...items]);
    } catch (e) {
      if (offset === 0) setLogs([]);
    }
    setLoading(false); setLoadingMore(false);
  }, [fUser, fAction, fDate]); // eslint-disable-line

  useEffect(() => {
    if (tab === "user") fetchLogs(0);
    else fetchCancels();
  }, [tab, fetchLogs, fetchCancels]);

  // Güvenilirlik özeti (kopma / kaydedilemedi) — seçili gün (veya bugün)
  useEffect(() => {
    if (tab !== "user") return;
    const params = {}; if (fDate) params.date = fDate;
    axios.get(`${API}/api/audit-logs/reliability-summary`, { headers, params })
      .then(r => setReliab(r.data)).catch(() => setReliab(null));
  }, [tab, fDate, logs]); // eslint-disable-line

  const clearFilters = () => { setFUser(""); setFAction(""); setFDate(""); setSearch(""); };
  const hasFilters = fUser || fAction || fDate;
  const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };

  // Arama: yüklü kayıtlar üzerinde anlık (tüm alanlarda) — Ctrl+F yerine
  const q = search.trim().toLowerCase();
  const displayed = !q ? logs : logs.filter(l => {
    const meta = ACTION_META[l.action] || {};
    const d = l.details || {};
    const menuStr = d.menu ? Object.entries(d.menu).map(([k, v]) => `${k} ${v}`).join(" ") : "";
    const exStr = Array.isArray(d.eklentiler) ? d.eklentiler.map(e => e.name).join(" ") : "";
    const hay = [
      l.user_name, l.user_email, l.customer_name, l.confirmation_code, l.boat_name,
      meta.label, l.action, fieldLabel(l.field), l.old_value, l.new_value,
      l.row_date, l.reason, Array.isArray(l.tags) ? l.tags.join(" ") : "", l.value, l.from, l.to,
      d.telefon, d.email, d.opsiyon, d.otel, d.acenta, d.aciklama, d.garson, d.salon, d.masa_no, menuStr, exStr,
    ].map(x => (x === null || x === undefined) ? "" : String(x).toLowerCase()).join(" | ");
    return hay.includes(q);
  });

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="audit-log-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center">
            <History className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Kullanıcı Geçmişi</h1>
            <p className="text-xs text-slate-400">Kim · Ne yaptı · Kimde/Nerede · Ne değişti · Ne zaman</p>
          </div>
        </div>
        <button data-testid="audit-refresh" onClick={() => tab === "user" ? fetchLogs(0) : fetchCancels()}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Yenile
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button data-testid="tab-user" onClick={() => setTab("user")}
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${tab === "user" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
          Kullanıcı Geçmişi
        </button>
        <button data-testid="tab-cancel" onClick={() => setTab("cancel")}
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${tab === "cancel" ? "bg-red-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
          İptal / Silme Geçmişi
        </button>
      </div>

      {tab === "cancel" ? (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-slate-400" />
            <input type="date" value={fDate} onChange={e => setFDate(e.target.value)}
              className="bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5" data-testid="cancel-filter-date" />
            {fDate && <button onClick={() => setFDate("")} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white"><X className="w-3 h-3" /> Temizle</button>}
            <span className="ml-auto text-xs text-slate-500" data-testid="cancel-count">{cancels.length} iptal olayı</span>
          </div>
          <div className="mb-3 text-xs text-slate-400 bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2">
            Bir rezervasyon AKTİF iken CANCELLED'a düştüğünde burada görünür. <b>Kaynak = Bókun Webhook / Senkron</b> ise sistem otomatik iptal etmiştir (insan değil); <b>Elle Silme</b> ise bir kullanıcı silmiştir.
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-700 bg-slate-800">
                  <th className="px-4 py-2.5 font-medium">Zaman</th>
                  <th className="px-4 py-2.5 font-medium">Kaynak</th>
                  <th className="px-4 py-2.5 font-medium">Rezervasyon</th>
                  <th className="px-4 py-2.5 font-medium">Müşteri</th>
                  <th className="px-4 py-2.5 font-medium">Tur Tarihi</th>
                  <th className="px-4 py-2.5 font-medium">Önceki Durum</th>
                </tr>
              </thead>
              <tbody data-testid="cancel-tbody">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Yükleniyor…</td></tr>
                ) : cancels.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">İptal olayı bulunamadı</td></tr>
                ) : cancels.map((c) => {
                  const SRC_META = {
                    webhook: { label: "Bókun Webhook", color: "#DC2626", icon: "fa-bolt" },
                    sync:    { label: "Senkron (Bókun)", color: "#D97706", icon: "fa-rotate" },
                    manual:  { label: "Elle Silme",     color: "#7C3AED", icon: "fa-trash" },
                  };
                  const m = SRC_META[c.source] || { label: c.source, color: "#6B7280", icon: "fa-circle" };
                  return (
                    <tr key={c.id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`cancel-row-${c.id}`}>
                      <td className="px-4 py-2.5 text-slate-300 whitespace-nowrap">{fmtTime(c.created_at || c.ts)}</td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full text-white" style={{ backgroundColor: m.color }}>
                          <i className={`fa-solid ${m.icon}`} /> {m.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-300">{c.confirmation_code || `#${c.booking_id}`}</td>
                      <td className="px-4 py-2.5 text-slate-300">{c.customer_name || "-"}</td>
                      <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{c.booking_date || "-"}</td>
                      <td className="px-4 py-2.5 text-slate-400">{c.prev_status || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
      <>
      {reliab && (reliab.totals?.drops > 0 || reliab.totals?.fails > 0) && (
        <div className="mb-4 rounded-xl border border-orange-800/40 bg-orange-950/20 p-3" data-testid="reliability-strip">
          <div className="flex items-center gap-2 mb-2">
            <i className="fa-solid fa-wifi text-orange-400 text-sm" />
            <span className="text-sm font-semibold text-orange-200">Bağlantı & Kayıt Sağlığı</span>
            <span className="text-xs text-slate-400">— {reliab.date}{!fDate ? " (bugün)" : ""}</span>
            <span className="ml-auto text-xs text-slate-300">
              Toplam: <b className="text-orange-300">{reliab.totals.drops}</b> kopma · <b className="text-red-300">{reliab.totals.fails}</b> kaydedilemedi
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {reliab.users.map((u) => (
              <div key={u.user_email} className="flex items-center gap-2 text-xs bg-slate-800/70 border border-slate-700 rounded-lg px-2.5 py-1.5" data-testid={`reliab-user-${u.user_email}`}>
                <span className="text-slate-200 font-medium">{u.user_name}</span>
                {u.drops > 0 && (
                  <span className="inline-flex items-center gap-1 text-orange-300" title="Bağlantı kopması">
                    <i className="fa-solid fa-wifi text-[10px]" /> {u.drops}{u.drop_ms ? ` (${Math.round(u.drop_ms / 1000)}sn)` : ""}
                  </span>
                )}
                {u.fails > 0 && (
                  <span className="inline-flex items-center gap-1 text-red-300" title="Kaydedilemeyen giriş">
                    <i className="fa-solid fa-triangle-exclamation text-[10px]" /> {u.fails}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Ara: müşteri, kod, kullanıcı, tekne, değişiklik…"
            className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-9 pr-8 py-2 placeholder:text-slate-500 focus:border-blue-500 outline-none"
            data-testid="audit-search" />
          {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white" data-testid="audit-search-clear"><X className="w-4 h-4" /></button>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Filter className="w-4 h-4 text-slate-400" />
        <div className="relative">
          <User className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <select value={fUser} onChange={e => setFUser(e.target.value)}
            className="appearance-none bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-8 pr-8 py-1.5" data-testid="audit-filter-user">
            <option value="">Tüm kullanıcılar</option>
            {staff.map(s => <option key={s.id} value={s.email || s.name}>{s.name}{s.role ? ` (${ROLE_LABEL[s.role] || s.role})` : ""}</option>)}
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <select value={fAction} onChange={e => setFAction(e.target.value)}
          className="bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5" data-testid="audit-filter-action">
          <option value="">Tüm işlemler</option>
          {Object.entries(ACTION_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <input type="date" value={fDate} onChange={e => setFDate(e.target.value)}
          className="bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5" data-testid="audit-filter-date" />
        <button onClick={() => setFDate(todayStr())} data-testid="audit-today-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm">
          <CalendarDays className="w-4 h-4" /> Bugün
        </button>
        {hasFilters && (
          <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid="audit-clear-filters">
            <X className="w-3 h-3" /> Temizle
          </button>
        )}
        <span className="ml-auto text-xs text-slate-500" data-testid="audit-count">
          {q ? `Arama: ${displayed.length} / ` : ""}{logs.length}{total != null ? ` / ${total} kayıt` : " kayıt"}{total != null && logs.length < total ? " (eksik — 'Daha fazla')" : ""}{fDate ? " · tüm gün" : ""}
        </span>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-700 bg-slate-800">
              <th className="px-4 py-2.5 font-medium whitespace-nowrap">Zaman</th>
              <th className="px-4 py-2.5 font-medium">Kullanıcı</th>
              <th className="px-4 py-2.5 font-medium">İşlem</th>
              <th className="px-4 py-2.5 font-medium">Müşteri / Rezervasyon</th>
              <th className="px-4 py-2.5 font-medium">Tekne</th>
              <th className="px-4 py-2.5 font-medium">Ne değişti</th>
              <th className="px-4 py-2.5 font-medium whitespace-nowrap">Tur Tarihi</th>
            </tr>
          </thead>
          <tbody data-testid="audit-log-tbody">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Yükleniyor…</td></tr>
            ) : displayed.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">{q ? "Aramayla eşleşen kayıt yok" : "Kayıt bulunamadı"}</td></tr>
            ) : displayed.map((log) => {
              const meta = ACTION_META[log.action] || { label: log.action, color: "#6B7280", icon: "fa-circle" };
              return (
                <tr key={log.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 align-top" data-testid={`audit-row-${log.id}`}>
                  <td className="px-4 py-2.5 text-slate-300 whitespace-nowrap">{fmtTime(log.created_at || log.ts)}</td>
                  <td className="px-4 py-2.5">
                    <div className="text-slate-200">{log.user_name || log.user_email}</div>
                    <div className="text-[11px] text-slate-500">{log.user_email}</div>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-300">{ROLE_LABEL[log.user_role] || log.user_role}</span>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full text-white" style={{ backgroundColor: meta.color }}>
                      <i className={`fa-solid ${meta.icon}`} /> {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 max-w-[200px]"><CustomerCell log={log} /></td>
                  <td className="px-4 py-2.5">
                    {log.boat_name ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-cyan-300 bg-cyan-950/40 border border-cyan-800/40 px-2 py-1 rounded-lg">
                        <Ship className="w-3 h-3" /> {log.boat_name}
                      </span>
                    ) : <span className="text-slate-600 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-300">{describe(log)}</td>
                  <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{log.row_date || "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!loading && hasMore && (
        <div className="flex justify-center mt-4">
          <button data-testid="audit-load-more" onClick={() => fetchLogs(logs.length)} disabled={loadingMore}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm disabled:opacity-50">
            {loadingMore ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ChevronDown className="w-4 h-4" />}
            {loadingMore ? "Yükleniyor…" : "Daha fazla yükle"}
          </button>
        </div>
      )}
      </>
      )}
    </div>
  );
}
